class DynamicProductPack extends HTMLElement {
  constructor() {
    super();

    this.steps = Array.from(this.querySelectorAll('[data-pack-step]'));
    this.submitButton = this.querySelector('[data-pack-submit]');
    this.buttonPrice = this.querySelector('[data-pack-button-price]');
    this.buttonComparePrice = this.querySelector('[data-pack-button-compare-price]');
    this.productPrice = document.querySelector('#text-price');
    this.productComparePrice = this.productPrice?.parentElement?.querySelector('.t-font-extralight');
    this.createdProductComparePrice = false;
    this.originalProductPrice = this.productPrice?.innerHTML || '';
    this.originalCompareHtml = this.productComparePrice?.innerHTML || '';
    this.originalCompareDisplay = this.productComparePrice?.style.display || '';
    this.selection = {};
    this.skippedSteps = new Set();

    this.bindEvents();
    this.initializePreselectedItems();
    this.updateState();
  }

  bindEvents() {
    this.querySelectorAll('[data-pack-item]').forEach((item) => {
      item.addEventListener('click', () => this.selectItem(item));
    });

    this.querySelectorAll('.dynamic-product-pack__step-title').forEach((title) => {
      title.addEventListener('click', () => {
        const step = title.closest('[data-pack-step]');
        if (!step || (!step.classList.contains('done') && !step.classList.contains('active'))) return;

        const stepIndex = this.steps.indexOf(step);
        this.resetStepsAfter(stepIndex);
        this.goToStep(stepIndex);
        this.updateState();
      });
    });

    this.querySelectorAll('[data-pack-continue]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.closest('[data-pack-step]');
        if (!button.classList.contains('enabled') || !step) return;

        this.goToStep(this.steps.indexOf(step) + 1);
      });
    });

    this.querySelectorAll('[data-pack-skip]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.closest('[data-pack-step]');
        if (!step) return;

        const stepIndex = step.dataset.packStep;
        this.selection[stepIndex] = null;
        this.skippedSteps.add(stepIndex);
        step.querySelectorAll('[data-pack-item]').forEach((item) => item.classList.remove('selected'));
        this.updateSelectedName(step, null);
        step.querySelector('[data-pack-continue]')?.classList.add('enabled');
        this.goToStep(this.steps.indexOf(step) + 1);
        this.updateState();
      });
    });

    this.submitButton?.addEventListener('click', () => this.submitPack());
  }

  selectItem(item) {
    const step = item.closest('[data-pack-step]');
    if (!step) return;

    const stepIndex = step.dataset.packStep;
    step.querySelectorAll('[data-pack-item]').forEach((otherItem) => otherItem.classList.remove('selected'));
    item.classList.add('selected');
    this.selection[stepIndex] = item;
    this.skippedSteps.delete(stepIndex);
    step.querySelector('[data-pack-continue]')?.classList.add('enabled');
    this.updateSelectedName(step, item);
    this.updateGalleryFromItem(item);

    this.updateState();
  }

  initializePreselectedItems() {
    this.steps.forEach((step) => {
      if (step.dataset.preselectFirst !== 'true') return;

      const item = step.querySelector('[data-pack-item].selected') || step.querySelector('[data-pack-item]');
      if (!item) return;

      const stepIndex = step.dataset.packStep;
      item.classList.add('selected');
      this.selection[stepIndex] = item;
      this.skippedSteps.delete(stepIndex);
      step.querySelector('[data-pack-continue]')?.classList.add('enabled');
      this.updateSelectedName(step, item);

      if (step.classList.contains('active')) {
        this.updateGalleryFromItem(item);
      }
    });
  }

  updateSelectedName(step, item) {
    const name = step.querySelector('[data-pack-selected-name]');
    const value = name?.querySelector('span');
    if (!name || !value) return;

    if (item?.dataset.productTitle) {
      value.textContent = item.dataset.productTitle;
      name.classList.remove('hidden');
    } else {
      value.textContent = '';
      name.classList.add('hidden');
    }
  }

  goToStep(index) {
    if (index >= this.steps.length) {
      this.steps.forEach((step) => {
        step.classList.remove('active');
        step.classList.add('done');
      });
      return;
    }

    const nextIndex = Math.min(index, this.steps.length - 1);

    this.steps.forEach((step, stepIndex) => {
      step.classList.toggle('active', stepIndex === nextIndex);
      step.classList.toggle('done', stepIndex < nextIndex);
    });

    const selectedItem = this.steps[nextIndex]?.querySelector('[data-pack-item].selected');
    if (selectedItem) {
      this.updateGalleryFromItem(selectedItem);
    }
  }

  resetStepsAfter(index) {
    this.steps.slice(index + 1).forEach((step) => {
      const stepIndex = step.dataset.packStep;
      delete this.selection[stepIndex];
      this.skippedSteps.delete(stepIndex);
      step.querySelectorAll('[data-pack-item]').forEach((item) => item.classList.remove('selected'));
      step.querySelector('[data-pack-continue]')?.classList.remove('enabled');
      this.updateSelectedName(step, null);
    });
  }

  updateState() {
    const total = this.getTargetTotal();
    const compareTotal = this.getCompareTotal();
    const formattedTotal = total > 0 ? this.formatMoney(total) : '';
    const formattedCompareTotal = this.hasComparePrice() && compareTotal > 0 ? this.formatMoney(compareTotal) : '';

    if (this.buttonPrice) this.buttonPrice.innerHTML = formattedTotal;
    this.updateCompareElement(this.buttonComparePrice, formattedCompareTotal);

    if (this.productPrice) {
      this.productPrice.innerHTML = formattedTotal || this.originalProductPrice;
    }

    if (formattedCompareTotal) {
      this.ensureProductComparePrice();
      if (this.productComparePrice) {
        this.productComparePrice.innerHTML = `<s>${formattedCompareTotal}</s>`;
        this.productComparePrice.style.display = '';
      }
    } else if (this.productComparePrice) {
      if (this.createdProductComparePrice) {
        this.productComparePrice.remove();
        this.productComparePrice = null;
        this.createdProductComparePrice = false;
      } else {
        this.productComparePrice.innerHTML = this.originalCompareHtml;
        this.productComparePrice.style.display = formattedTotal ? 'none' : this.originalCompareDisplay;
      }
    }

    if (this.submitButton) {
      this.submitButton.disabled = !this.isComplete();
    }
  }

  getTargetTotal() {
    let total = 0;
    const firstStepItem = this.selection['1'];

    if (firstStepItem) {
      total += Number(firstStepItem.dataset.packPrice || 0);
    }

    this.steps.forEach((step) => {
      if (step.dataset.packOptional !== 'true') return;
      const item = this.selection[step.dataset.packStep];
      if (item) total += Number(step.dataset.packStepPrice || 0);
    });

    return total;
  }

  ensureProductComparePrice() {
    if (this.productComparePrice || !this.productPrice?.parentElement) return;

    const comparePrice = document.createElement('span');
    comparePrice.className = 't-font-extralight';
    comparePrice.style.color = this.productPrice.style.color || 'inherit';
    comparePrice.style.marginRight = '8px';
    this.productPrice.parentElement.insertBefore(comparePrice, this.productPrice);
    this.productComparePrice = comparePrice;
    this.createdProductComparePrice = true;
  }

  getCompareTotal() {
    let total = 0;
    const firstStepItem = this.selection['1'];

    if (firstStepItem) {
      const comparePrice = Number(firstStepItem.dataset.packComparePrice || 0);
      total += comparePrice > 0 ? comparePrice : Number(firstStepItem.dataset.packPrice || 0);
    }

    this.steps.forEach((step) => {
      if (step.dataset.packOptional !== 'true') return;
      const item = this.selection[step.dataset.packStep];
      if (item) {
        const comparePrice = Number(step.dataset.packStepComparePrice || 0);
        total += comparePrice > 0 ? comparePrice : Number(step.dataset.packStepPrice || 0);
      }
    });

    return total;
  }

  hasComparePrice() {
    const firstStepItem = this.selection['1'];

    if (Number(firstStepItem?.dataset.packComparePrice || 0) > 0) {
      return true;
    }

    return this.steps.some((step) => {
      if (step.dataset.packOptional !== 'true') return false;
      if (!this.selection[step.dataset.packStep]) return false;

      return Number(step.dataset.packStepComparePrice || 0) > 0;
    });
  }

  updateCompareElement(element, value) {
    if (!element) return;

    element.innerHTML = value;
    element.hidden = !value;
  }

  updateGalleryFromItem(item) {
    const json = item.querySelector('[data-pack-media-json]');
    if (!json) return;

    try {
      const media = JSON.parse(json.textContent).filter((image) => image && image.src);
      if (media.length) {
        this.renderProductMedia(media);
      }
    } catch (error) {
      console.error('Dynamic pack media JSON parse error', error);
    }
  }

  renderProductMedia(media) {
    const productMedia = document.querySelector('product-media');
    const mainSlider = productMedia?.querySelector('.main-slider');
    const mainWrapper = mainSlider?.querySelector('.swiper-wrapper');
    const thumbSlider = productMedia?.querySelector('.js-thumbnail-slider');
    const thumbWrapper = thumbSlider?.querySelector('.swiper-wrapper');
    const controls = productMedia?.querySelector('.swiper-controls');
    const arrows = productMedia?.querySelector('.thumbnail-slider.btn-slide');

    if (!productMedia || !mainWrapper) return;

    productMedia.dataset.size = media.length;
    productMedia.querySelectorAll('.cover-image').forEach((image) => {
      image.style.display = 'none';
    });
    productMedia.childImages = [];

    mainWrapper.innerHTML = media
      .map((image, index) => this.renderMainSlide(image, index))
      .join('');

    if (thumbWrapper) {
      thumbWrapper.innerHTML = media
        .map((image, index) => this.renderThumbnailSlide(image, index))
        .join('');
    }

    const hasMultipleImages = media.length > 1;
    if (thumbSlider) thumbSlider.style.display = hasMultipleImages ? '' : 'none';
    if (controls) controls.style.display = hasMultipleImages ? '' : 'none';
    if (arrows) arrows.style.display = hasMultipleImages ? '' : 'none';

    const mainSwiper = mainSlider?.swiper || (typeof mainSlide !== 'undefined' ? mainSlide : null);
    const thumbSwiper = thumbSlider?.swiper || mainSwiper?.thumbs?.swiper;

    if (thumbSwiper && !thumbSwiper.destroyed) {
      thumbSwiper.update();
      thumbSwiper.slideTo(0, 0);
    }

    if (mainSwiper && !mainSwiper.destroyed) {
      mainSwiper.update();
      mainSwiper.slideTo(0, 0);
      if (mainSwiper.updateAutoHeight) mainSwiper.updateAutoHeight(0);
      if (mainSwiper.thumbs?.update) mainSwiper.thumbs.update(true);
    }
  }

  renderMainSlide(image, index) {
    const activeClass = index === 0 ? ' is-active' : '';
    return `
      <li
        class="product__media-item swiper-slide${activeClass}"
        data-media-id="dynamic-pack-${image.id}"
      >
        <img
          loading="lazy"
          src="${this.escapeHtml(image.src)}"
          srcset="${this.escapeHtml(image.srcset || '')}"
          alt="${this.escapeHtml(image.alt || '')}"
          width="${this.escapeHtml(String(image.width || ''))}"
          height="${this.escapeHtml(String(image.height || ''))}"
        >
      </li>
    `;
  }

  renderThumbnailSlide(image, index) {
    return `
      <li
        class="thumbnail-list__item swiper-slide"
        data-target="dynamic-pack-${image.id}"
        data-media-position="${index + 1}"
      >
        <button
          type="button"
          class="thumbnail global-media-settings global-media-settings--no-shadow"
          aria-label="Charger l'image ${index + 1}"
          ${index === 0 ? 'aria-current="true"' : ''}
          data-media-position="${index + 1}"
        >
          <img
            src="${this.escapeHtml(image.thumbSrc || image.src)}"
            srcset="${this.escapeHtml(image.thumbSrcset || image.srcset || '')}"
            sizes="(min-width: 750px) 80px, 0"
            alt="${this.escapeHtml(image.alt || '')}"
            height="80"
            width="80"
            loading="lazy"
          >
        </button>
      </li>
    `;
  }

  escapeHtml(value) {
    return value.replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  isComplete() {
    return this.steps.every((step) => {
      const stepIndex = step.dataset.packStep;
      return this.selection[stepIndex] || (step.dataset.packOptional === 'true' && this.skippedSteps.has(stepIndex));
    });
  }

  submitPack() {
    if (!this.isComplete()) return;

    const targetTotal = this.getTargetTotal();
    const packKey = `dynamic-pack-${Date.now()}`;
    const items = Object.keys(this.selection)
      .sort((a, b) => Number(a) - Number(b))
      .map((stepIndex) => this.selection[stepIndex])
      .filter(Boolean)
      .map((item) => ({
        id: Number(item.dataset.variantId),
        quantity: 1,
        properties: {
          _dynamic_pack: true,
          _dynamic_pack_key: packKey,
          _dynamic_pack_step: item.dataset.step,
          _dynamic_pack_target_price: targetTotal,
        },
      }));

    if (!items.length) return;

    this.submitButton.classList.add('loading');
    this.submitButton.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items,
        sections: Shopify.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.text())
      .then((data) => {
        const parsedState = JSON.parse(data);

        Shopify.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);

          elementToReplace.innerHTML = Shopify.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        document.querySelector('.cart-drawer__opener .js-nav--btn')?.click();
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        this.updateState();
      });
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }

    return `${(cents / 100).toFixed(2).replace('.', ',')}€`;
  }
}

if (!customElements.get('dynamic-product-pack')) {
  customElements.define('dynamic-product-pack', DynamicProductPack);
}
